// @ts-nocheck
import React, { useEffect, useId } from 'react';
import { Copy, XCircle } from 'lucide-react';

export type PasswordResetLinkDialogProps = {
  open: boolean;
  email: string;
  passwordResetLink: string;
  onClose: () => void;
};

async function copyText(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
}

/**
 * Admin-facing dialog: share a time-limited reset URL (same pattern as perk-loyalty-app).
 */
export default function PasswordResetLinkDialog({
  open,
  email,
  passwordResetLink,
  onClose,
}: PasswordResetLinkDialogProps) {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-lg rounded-xl bg-white shadow-xl border border-gray-200"
      >
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900">
            Password reset link
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            aria-label="Close"
          >
            <XCircle size={20} aria-hidden />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Share this link with <strong className="text-gray-900">{email}</strong>. It expires in 7 days. Issuing a new
            link replaces any previous unused link for this user.
          </p>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Reset link</p>
              <button
                type="button"
                onClick={() => void copyText(passwordResetLink)}
                className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900"
              >
                <Copy size={14} aria-hidden />
                Copy
              </button>
            </div>
            <p className="text-sm text-gray-800 break-all font-mono">{passwordResetLink}</p>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-gray-200 flex justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg btn-primary text-sm font-medium">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
